import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { CommunicationTemplateService, CommunicationTemplate } from '../../services/communication-template.service';
import { AuthService, UserRole } from '../../auth/auth.service';
import { RCOversightService, CompanyDropdownOption } from '../../services/rc-oversight.service';
import { ErrorService } from '../../shared/services/error.service';

@Component({
  selector: 'app-template-editor',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './template-editor.component.html',
  styleUrls: ['./template-editor.component.css']
})
export class TemplateEditorComponent implements OnInit, OnDestroy {
  @Input() template: CommunicationTemplate | null = null;
  @Input() isEdit: boolean = false;
  @Output() save = new EventEmitter<CommunicationTemplate>();
  @Output() cancel = new EventEmitter<void>();
  
  @ViewChild('templateBodyInput') templateBodyInput!: ElementRef<HTMLTextAreaElement>;

  formData = {
    name: '',
    subject: '',
    body: '',
    template_type: 'sms' as 'sms' | 'email' | 'whatsapp',
    mcCompanyId: ''
  };

  isLoading: boolean = false;
  variables: string[] = [];
  mcCompanies: CompanyDropdownOption[] = [];
  loadingCompanies = false;
  
  private subscriptions = new Subscription();

  channels = [
    { value: 'sms', label: 'SMS' },
    { value: 'email', label: 'Email' },
    { value: 'whatsapp', label: 'WhatsApp' }
  ];

  constructor(
    private communicationTemplateService: CommunicationTemplateService,
    private authService: AuthService,
    private rcOversightService: RCOversightService,
    private errorService: ErrorService
  ) {}

  ngOnInit() {
    if (this.template && this.isEdit) {
      this.formData = {
        name: this.template.name,
        subject: this.template.subject || '',
        body: this.template.body,
        template_type: this.template.template_type,
        mcCompanyId: (this.template as any).mcCompanyId || ''
      };
    }
    this.extractVariables();
    this.loadMCCompanies();
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  onChannelChange() {
    // Clear subject when switching away from email
    if (this.formData.template_type !== 'email') {
      this.formData.subject = '';
    }
  }

  onBodyChange() {
    this.extractVariables();
  }

  extractVariables() {
    this.variables = this.communicationTemplateService.extractVariables(this.formData.body);
  }

  getCharacterCount(): number {
    return this.formData.body.length;
  }

  getCharacterLimit(): number {
    switch (this.formData.template_type) {
      case 'sms': return 160;
      case 'whatsapp': return 1600;
      case 'email': return 2000;
      default: return 160;
    }
  }

  isOverLimit(): boolean {
    return this.getCharacterCount() > this.getCharacterLimit();
  }


  onSubmit() {
    if (!this.formData.name.trim() || !this.formData.body.trim()) {
      this.errorService.showError('Please fill in all required fields');
      return;
    }

    if (this.formData.template_type === 'email' && !this.formData.subject.trim()) {
      this.errorService.showError('Email templates require a subject line');
      return;
    }

    // RC users must select a company
    if (this.isSuperAdmin() && !this.formData.mcCompanyId) {
      this.errorService.showError('Please select a company for this template');
      return;
    }

    this.isLoading = true;

    const templateData: Partial<CommunicationTemplate> = {
      name: this.formData.name.trim(),
      body: this.formData.body.trim(),
      template_type: this.formData.template_type,
      channel: this.formData.template_type,
      variables: this.variables,
      character_count: this.getCharacterCount()
    };

    if (this.formData.template_type === 'email') {
      templateData.subject = this.formData.subject.trim();
    }

    // Include MC company ID if Super Admin and value is selected
    if (this.isSuperAdmin() && this.formData.mcCompanyId) {
      (templateData as any).mcCompanyId = this.formData.mcCompanyId;
    }

    const operation = this.isEdit && this.template
      ? this.communicationTemplateService.updateTemplate(this.template._id, templateData)
      : this.communicationTemplateService.createTemplate(templateData);

    operation.subscribe({
      next: (response) => {
        if (response.success) {
          const savedTemplate = Array.isArray(response.data) ? response.data[0] : response.data;
          this.errorService.showInfo('Template saved successfully', true, 3000);
          this.save.emit(savedTemplate);
        } else {
          this.errorService.showError('Failed to save template: ' + response.message);
        }
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Failed to save template:', error);
        this.errorService.handleHttpError(error, 'Failed to save template. Please try again.');
        this.isLoading = false;
      }
    });
  }

  onCancel() {
    this.cancel.emit();
  }

  insertVariable(variableKey: string) {
    const textarea = this.templateBodyInput?.nativeElement;
    if (!textarea) {
      // Fallback: append to end of body
      this.formData.body += `{{${variableKey}}}`;
      this.extractVariables();
      return;
    }

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const variableText = `{{${variableKey}}}`;
    
    const newBody = this.formData.body.substring(0, start) + 
                   variableText + 
                   this.formData.body.substring(end);
    
    this.formData.body = newBody;
    this.extractVariables();

    // Set cursor position after the inserted variable
    setTimeout(() => {
      const newPosition = start + variableText.length;
      textarea.setSelectionRange(newPosition, newPosition);
      textarea.focus();
    });
  }

  // Common variable suggestions
  commonVariables = [
    { key: 'candidate_name', display: 'Candidate Name' },
    { key: 'phone_number', display: 'Phone Number' },
    { key: 'email', display: 'Email' },
    { key: 'role', display: 'Role' },
    { key: 'appointment_date', display: 'Appointment Date' },
    { key: 'appointment_time', display: 'Appointment Time' },
    { key: 'service_type', display: 'Service Type' },
    { key: 'location', display: 'Location' },
    { key: 'duration', display: 'Duration' }
  ];

  isSuperAdmin(): boolean {
    const currentUser = this.authService.currentUserValue;
    return currentUser?.role === UserRole.OWNER && 
           currentUser?.company_type === 'RC';
  }

  loadMCCompanies() {
    if (!this.isSuperAdmin()) {
      return;
    }
    
    this.loadingCompanies = true;
    this.subscriptions.add(
      this.rcOversightService.getMCCompaniesDropdown().subscribe({
        next: (companies) => {
          this.mcCompanies = companies || [];
          this.loadingCompanies = false;
        },
        error: (error) => {
          console.error('Error loading MC companies:', error);
          this.mcCompanies = [];
          this.loadingCompanies = false;
        }
      })
    );
  }
}